/**
 * Backtest Service
 * 
 * Service layer for fetching backtest data from the API.
 * Falls back to mock data if API fails or USE_MOCK_DATA is true.
 */

import { backtestService as apiBacktestService, backtestJobService as apiJobService } from '../config/api';
import { transformBacktestResult, transformBacktestList } from '../utils/backtestDataTransformer';
import { mockBacktestData } from '../data/mockBacktestData';

// Set to true to use mock data instead of API (for development/testing)
const USE_MOCK_DATA = false;

// Job status constants
export const JOB_STATUS = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
};

/**
 * Fetch complete backtest results
 * @param {string} backtestId - Backtest ID to fetch (optional, fetches latest if not provided)
 * @param {Object} options - Optional filters
 * @returns {Promise<Object>} Backtest data
 */
export const fetchBacktestResults = async (backtestId = null, options = {}) => {
  if (USE_MOCK_DATA) {
    return { ...mockBacktestData };
  }

  try {
    let response;

    if (backtestId) {
      // Fetch specific backtest by ID
      response = await apiBacktestService.getById(backtestId);
      return transformBacktestResult(response.data);
    } else {
      // Fetch latest backtest from history
      response = await apiBacktestService.getHistory();
      const backtests = response.data;

      if (backtests && backtests.length > 0) {
        // Get the most recent backtest's full details
        const latestId = backtests[0].id || backtests[0].backtestId;
        const detailResponse = await apiBacktestService.getById(latestId);
        return transformBacktestResult(detailResponse.data);
      }

      // No backtests found - return null
      return null;
    }
  } catch (error) {
    console.error('Error fetching backtest results:', error);
    throw error;
  }
};

/**
 * Fetch backtest history list
 * @returns {Promise<Array>} List of backtest summaries
 */
export const fetchBacktestHistory = async () => {
  if (USE_MOCK_DATA) {
    return [{ ...mockBacktestData, id: 'mock-1' }];
  }

  try {
    const response = await apiBacktestService.getHistory();
    return transformBacktestList(response.data);
  } catch (error) {
    console.error('Error fetching backtest history:', error);
    throw error;
  }
};

/**
 * Fetch backtests for a specific strategy
 * @param {string} strategyId 
 * @returns {Promise<Array>}
 */
export const fetchBacktestsByStrategy = async (strategyId) => {
  if (USE_MOCK_DATA) {
    return [{ ...mockBacktestData, id: 'mock-1', strategyId }];
  }

  try {
    const response = await apiBacktestService.getByStrategy(strategyId);
    return transformBacktestList(response.data);
  } catch (error) {
    console.error('Error fetching backtests for strategy:', error);
    throw error;
  }
};

/**
 * Delete a backtest result
 * @param {string} backtestId 
 * @returns {Promise<void>}
 */
export const deleteBacktest = async (backtestId) => {
  if (USE_MOCK_DATA) {
    return;
  }

  try {
    await apiBacktestService.delete(backtestId);
  } catch (error) {
    console.error('Error deleting backtest:', error);
    throw error;
  }
};

/**
 * Fetch equity series only
 * @param {string} backtestId 
 * @returns {Promise<Array>}
 */
export const fetchEquitySeries = async (backtestId = null) => {
  const data = await fetchBacktestResults(backtestId);
  return data?.equitySeries || [];
};

/**
 * Fetch returns series only
 * @param {string} backtestId 
 * @returns {Promise<Array>}
 */
export const fetchReturnsSeries = async (backtestId = null) => {
  const data = await fetchBacktestResults(backtestId);
  return data?.returnsSeries || [];
};

/**
 * Fetch trade log
 * @param {string} backtestId 
 * @returns {Promise<Array>}
 */
export const fetchTrades = async (backtestId = null) => {
  const data = await fetchBacktestResults(backtestId);
  return data?.trades || [];
};

/**
 * Fetch price series with trade signals
 * @param {string} backtestId 
 * @returns {Promise<Object>}
 */
export const fetchPriceWithSignals = async (backtestId = null) => {
  const data = await fetchBacktestResults(backtestId);
  return {
    priceSeries: data?.priceSeries || [],
    trades: data?.trades || [],
    trendLines: data?.trendLines || []
  };
};

/**
 * Fetch summary metrics
 * @param {string} backtestId 
 * @returns {Promise<Object>}
 */
export const fetchSummary = async (backtestId = null) => {
  const data = await fetchBacktestResults(backtestId);
  return data?.summary || null;
};

/**
 * Fetch benchmark comparison data
 * @param {string} backtestId 
 * @returns {Promise<Object>}
 */
export const fetchBenchmarkComparison = async (backtestId = null) => {
  const data = await fetchBacktestResults(backtestId);
  return {
    equitySeries: data?.equitySeries || [],
    benchmarkSeries: data?.benchmarkSeries || []
  };
};

// ============ Async Job Functions ============

/**
 * Submit an async backtest job
 * @param {string} strategyId - Strategy ID
 * @param {string} strategyName - Strategy name for display
 * @param {Object} params - Backtest parameters
 * @returns {Promise<Object>} Job object with id and status
 */
export const submitAsyncBacktest = async (strategyId, strategyName, params) => {
  try {
    const response = await apiJobService.submit(strategyId, strategyName, params);
    return response.data;
  } catch (error) {
    console.error('Error submitting async backtest:', error);
    throw error;
  }
};

/**
 * Get all backtest jobs for the user
 * @returns {Promise<Array>} List of jobs
 */
export const fetchBacktestJobs = async () => {
  try {
    const response = await apiJobService.list();
    return response.data || [];
  } catch (error) {
    console.error('Error fetching backtest jobs:', error);
    throw error;
  }
};

/**
 * Get job progress
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Progress info
 */
export const fetchJobProgress = async (jobId) => {
  try {
    const response = await apiJobService.getProgress(jobId);
    return response.data;
  } catch (error) {
    console.error('Error fetching job progress:', error);
    throw error;
  }
};

/**
 * Poll job until completion (or failure/cancellation)
 * @param {string} jobId - Job ID
 * @param {Object} options - Polling options
 * @param {number} options.interval - Poll interval in ms (default: 2000)
 * @param {number} options.timeout - Max wait time in ms (default: 300000 = 5 min)
 * @param {Function} options.onProgress - Callback for progress updates
 * @returns {Promise<Object>} Final job result
 */
export const pollJobUntilComplete = async (jobId, options = {}) => {
  const {
    interval = 2000,
    timeout = 300000,
    onProgress = null
  } = options;

  const startTime = Date.now();

  while (true) {
    const progress = await fetchJobProgress(jobId);

    if (onProgress) {
      onProgress(progress);
    }

    // Check terminal states
    if (progress.status === JOB_STATUS.COMPLETED) {
      return progress;
    }
    if (progress.status === JOB_STATUS.FAILED) {
      throw new Error(progress.errorMessage || 'Backtest job failed');
    }
    if (progress.status === JOB_STATUS.CANCELLED) {
      throw new Error('Backtest job was cancelled');
    }

    // Check timeout
    if (Date.now() - startTime > timeout) {
      throw new Error('Backtest job timed out');
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, interval));
  }
};

/**
 * Cancel a running job
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Cancelled job
 */
export const cancelBacktestJob = async (jobId) => {
  try {
    const response = await apiJobService.cancel(jobId);
    return response.data;
  } catch (error) {
    console.error('Error cancelling backtest job:', error);
    throw error;
  }
};

/**
 * Fetch results for a completed async job
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Series data
 */
export const fetchJobSeriesData = async (jobId) => {
  try {
    const response = await apiJobService.getSeriesData(jobId);
    return response.data;
  } catch (error) {
    console.error('Error fetching job series data:', error);
    throw error;
  }
};

// Export as default object for convenience
const backtestService = {
  fetchBacktestResults,
  fetchBacktestHistory,
  fetchBacktestsByStrategy,
  deleteBacktest,
  fetchEquitySeries,
  fetchReturnsSeries,
  fetchTrades,
  fetchPriceWithSignals,
  fetchSummary,
  fetchBenchmarkComparison,
  // Async job functions
  submitAsyncBacktest,
  fetchBacktestJobs,
  fetchJobProgress,
  pollJobUntilComplete,
  cancelBacktestJob,
  fetchJobSeriesData,
  // Constants
  JOB_STATUS,
};

export default backtestService;

